import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | null;
  hint?: ReactNode;
  hideLabel?: boolean;
}

export const inputClass =
  'block w-full min-h-12 rounded-xl border border-line bg-surface px-4 text-base text-ink placeholder:text-subtle shadow-sm transition-colors focus:border-accent focus:outline-none focus-visible:outline-none focus:ring-3 focus:ring-accent/25 aria-[invalid=true]:border-danger';

/** Labelled text input with inline error. Always >=16px so iOS doesn't zoom. */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, hideLabel, id, className = '', ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const errId = `${inputId}-err`;
  const hintId = `${inputId}-hint`;
  const describedBy = [error ? errId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={className}>
      <label htmlFor={inputId} className={hideLabel ? 'sr-only' : 'mb-1.5 block text-sm font-semibold text-ink'}>
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={inputClass}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-sm text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} className="mt-1.5 text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

export function FormError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <div role="alert" className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-[0.95rem] text-danger">
      {children}
    </div>
  );
}
